import { useParams, Link } from 'react-router-dom';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory, type Article } from '@/mocks/articles';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import NotFound from '@/pages/NotFound';

function ArticleCard({ article }: { article: Article }) {
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative w-full aspect-video overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 bg-black/60 backdrop-blur-sm text-white text-xs rounded-full whitespace-nowrap">
            {article.duration}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <span className="px-2 py-1 bg-primary-500 text-white text-xs rounded-full whitespace-nowrap font-medium">
            {article.quality}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {(article.readCount / 10000).toFixed(1)}万
          </span>
          <span>{article.publishDate}</span>
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const currentArticles = getArticlesByCategory(slug || '');

  if (!category) {
    return <NotFound />;
  }

  const categoryTitle = `${category.name} - 4688.tv 分类浏览`;
  const categoryDesc = `${category.description}。4688.tv 提供海量${category.name}内容，高清流畅播放，随时随地畅享。`;

  return (
    <>
      <SEOHead
        title={categoryTitle}
        description={categoryDesc}
        keywords={`4688.tv ${category.name},4688.tv ${category.name}在线观看,4688.tv ${category.name}高清,${category.name}在线视频`}
        canonicalUrl={`/category/${category.slug}`}
      />

      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://4688.tv' },
            { '@type': 'ListItem', position: 2, name: category.name, item: `https://4688.tv/category/${category.slug}` },
          ],
        }}
      />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb items={[{ label: category.name }]} />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              {category.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-500">{category.description}</p>
          </div>

          <div className="flex items-center gap-3 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {currentArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pb-16">
              {currentArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block" />
              <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}